/**
 * @file A yearly analytics goal at `goals/{goalId}`: the target one user, one team or the Org holds for one fiscal
 * year. A team goal's key is the id of a business group `parameters/analytics.teamBusinessGroups` marks as a team;
 * membership in a marked team group grants team-goal and teammate-goal visibility to metricsRead holders, and nothing
 * else. Written only by an enabled `metricsAdmin` and read by React under `firestore.rules`, which decide every
 * read except Org from the id `analyticsGoalId` builds, never from the stored fields, so a missing goal reads as not
 * found. Goals never enter the lake. `target` is a decimal string with at most two decimal places; `periodStart` and
 * `periodEnd` are `YYYY-MM-DD` dates in `parameters/analytics.calendar.timeZone`, the fiscal year start and the next
 * one's (exclusive); `createdAt` and `modifiedAt` are `Date#toISOString()` strings. A user goal carries no team, so a
 * team move writes only the user document.
 */
import type { DocId } from './System.types';

export const ANALYTICS_GOALS_COLLECTION = 'goals';

/** A user goal names `uid`, a team goal names the marked team business group `teamId`, the Org goal names neither. */
export type AnalyticsGoalSubject =
  | { subject: 'user'; uid: string; teamId: null }
  | { subject: 'team'; uid: null; teamId: DocId }
  | { subject: 'org'; uid: null; teamId: null };

export type AnalyticsGoal = AnalyticsGoalSubject & {
  target: string;
  periodStart: string;
  periodEnd: string;
  createdAt: string;
  modifiedAt: string;
};
